/**
 * pptx.js — translate a PowerPoint deck without touching its design.
 *
 * WHY IT IS HERE. Wordstat 19.09.2026: «перевод презентации» 8 753 impressions
 * a month and «перевести pptx» 2 927 — and until this file every one of those
 * visitors was turned away at the upload zone with "DOCX, PDF and EPUB only".
 * The paid competitors (DocTranslator, Doctranslate.io) all take .pptx; the
 * gap was ours alone.
 *
 * ⚠️ The number is the HONEST one, not the biggest one. «перевести презентацию»
 * shows 18 681 and was what I quoted first — but in Russian «перевести X в Y»
 * means CONVERT, and inside that phrase sits «перевести презентацию в пдф»
 * (12 911), a request for a different tool entirely. Caught on the second pass,
 * which is exactly the «высокочастотное ≠ наше» trap CLAUDE.md warns about.
 *
 * WHY IT IS SHORT. A .pptx is the same zip of XML a .docx is, and the same
 * reasoning applies: the visible words live in <a:t> nodes, so swapping those
 * leaves every theme, master, animation, image, chart geometry and text box
 * position untouched — none of them is ever read. The whole run-merging rule
 * lives in ooxml.js and is shared with docx.js; this file only says WHICH
 * parts of the zip carry words and in what order.
 *
 * WHICH PARTS, and why each:
 *   · ppt/slides/slideN.xml       — the deck itself, including tables;
 *   · ppt/notesSlides/…           — speaker notes, which are what the presenter
 *                                   actually reads out and what every "export
 *                                   the text" competitor drops;
 *   · ppt/charts/chartN.xml       — chart and axis titles, legend labels;
 *   · ppt/diagrams/dataN.xml      — SmartArt, whose text lives nowhere else.
 *
 * NOT slide masters and layouts. Their text is the placeholder prompt
 * ("Click to edit Master title style") that a reader never sees; translating it
 * spends the visitor's quota on invisible strings and, worse, pads the review
 * table with rows that correspond to nothing on screen.
 *
 * ORDER MATTERS. Slides are numbered in the file name, not sorted by it —
 * slide10.xml sorts before slide2.xml as a string. Reading order is what makes
 * a translation read as a deck rather than a bag of strings, and it is also
 * what the reviewer scrolls through, so the numeric part decides.
 *
 * Exposed as window.DTPptx.
 */
(function (root) {
    'use strict';
    if (root.DTPptx) return;

    var O = root.DTOoxml;
    var NS = O && O.A;

    /** Parts that carry visible words, in the order a reader meets them. */
    var GROUPS = [
        { re: /^ppt\/slides\/slide(\d+)\.xml$/, rank: 0 },
        { re: /^ppt\/notesSlides\/notesSlide(\d+)\.xml$/, rank: 1 },
        { re: /^ppt\/charts\/chart(\d+)\.xml$/, rank: 2 },
        { re: /^ppt\/diagrams\/data(\d+)\.xml$/, rank: 3 },
    ];

    function classify(name) {
        for (var i = 0; i < GROUPS.length; i++) {
            var m = GROUPS[i].re.exec(name);
            if (m) return { rank: GROUPS[i].rank, n: parseInt(m[1], 10) || 0 };
        }
        return null;
    }

    /**
     * Slides first and in deck order, then the notes that belong to them, then
     * the charts, then the SmartArt. `slide10` after `slide9`, not before it.
     */
    function orderedParts(zip) {
        var out = [];
        Object.keys(zip.files).forEach(function (name) {
            var c = classify(name);
            if (c) out.push({ name: name, rank: c.rank, n: c.n });
        });
        out.sort(function (a, b) { return a.rank - b.rank || a.n - b.n; });
        return out.map(function (p) { return p.name; });
    }

    /**
     * open(file, JSZip) → { units, meta, build(translated) }
     *
     * `units` is [{ id, text }] in reading order; `build` takes
     * { id: translatedText } and returns a Blob that PowerPoint, Keynote,
     * LibreOffice and Google Slides open as the deck it was.
     */
    async function open(file, JSZip) {
        if (!O) throw new Error('ooxml_missing');
        var zip = await JSZip.loadAsync(file);
        // The marker every .pptx has and no other OPC package does. Checking it
        // here rather than trusting the extension is what keeps a renamed .docx
        // from being read as an empty deck.
        if (!zip.file('ppt/presentation.xml')) {
            throw new Error('not_a_pptx');
        }

        var names = orderedParts(zip);
        var parts = {};
        var units = [];
        var slides = 0, notes = 0;

        for (var i = 0; i < names.length; i++) {
            var name = names[i];
            var xml = await zip.file(name).async('string');
            var r = O.unitsForPart(xml, name, NS);
            parts[name] = r.pieces;
            if (/^ppt\/slides\//.test(name)) slides++;
            if (/^ppt\/notesSlides\//.test(name) && r.units.length) notes++;
            r.units.forEach(function (u) {
                u.id = units.length;
                units.push(u);
            });
        }

        return {
            kind: 'pptx',
            units: units.map(function (u) { return { id: u.id, text: u.text }; }),
            meta: {
                parts: names,
                paragraphs: units.length,
                // Slides are the unit a person counts a deck in, so they are
                // reported where other formats report pages.
                pages: slides,
                notes: notes,
                flattened: units.filter(function (u) { return u.flattened; }).length,
                chars: units.reduce(function (n, u) { return n + u.text.length; }, 0),
            },

            async build(translated) {
                units.forEach(function (u) {
                    var text = translated[u.id];
                    if (text == null || text === '') return;
                    O.placeTranslation(parts[u.part], u, text);
                });

                names.forEach(function (nm) {
                    zip.file(nm, O.buildPart(parts[nm], NS));
                });

                return zip.generateAsync({
                    type: 'blob',
                    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    compression: 'DEFLATE',
                });
            },
        };
    }

    root.DTPptx = {
        open: open,
        // exposed for tests
        _orderedParts: orderedParts,
        _classify: classify,
    };
})(typeof self !== 'undefined' ? self : this);
